import {
  mapToKpiTrendChartData,
  useKpisMonthlyTrendsByTypes,
  useGetKpisByTypes,
  formatKpiLabel,
  getKpiTypeKey
} from '@domain/kpis'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CalculationTimeFrameType, KpiType, KpiVisibilityFlag} from '@hconnect/common/types'
import {cardBoxSizes} from '@hconnect/uikit'
import {CardBox} from '@hconnect/uikit/src/lib2'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {spawnErrorToast} from '@hm/fundamental'
import {Box, Typography} from '@mui/material'
import moment from 'moment-timezone'
import {useCallback, useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {CardChipsToggle, PlantStatusDailyKpis, PlantStatusKpisTrends} from '../components'
import {KPIS_WIDGET_NAMESPACE} from '../consts'
import {usePlantStatusCardChipsToggle, PlantStatusVariant} from '../hooks'

type Props = {
  plantId: string
  kpiTypes: KpiType[]
  kpiVisibilityFlag: KpiVisibilityFlag
}

const AGGREGATION_MONTHS_COUNT = 6
const cardBoxPx = {xs: 2, md: 3}

export const PlantStatusKpisContainer: React.FC<Props> = ({
  plantId,
  kpiTypes,
  kpiVisibilityFlag
}) => {
  const {t} = useTranslation(KPIS_WIDGET_NAMESPACE)
  const timezone = useUpmPlantTimezone(plantId)
  const to = moment.tz(timezone).subtract(1, 'month').endOf('month')
  const from = to
    .clone()
    .subtract(AGGREGATION_MONTHS_COUNT - 1, 'months')
    .startOf('month')

  const startOfYesterday = moment.utc().tz(timezone).subtract(1, 'days').startOf('day')
  const endOfYesterday = startOfYesterday.clone().endOf('day')

  const {
    value: cardVariant,
    onChange: handleCardVariantChange,
    options: cardVariants
  } = usePlantStatusCardChipsToggle(AGGREGATION_MONTHS_COUNT)

  const notifyFailedToFetchKpis = useCallback(
    () => spawnErrorToast(t('plantStatus.message.failedToFetchMonthlyKPIs'), {closeOnClick: true}),
    [t]
  )

  const {
    data: kpisMonthlyTrends,
    isLoading: areKpisTrendsLoading,
    isError: isKpisTrendsError,
    isRefetching: areKpisTrendsRefetching,
    refetch: refetchKpisTrends
  } = useKpisMonthlyTrendsByTypes(
    {
      plantId,
      kpiTypes,
      from,
      to
    },
    {onError: notifyFailedToFetchKpis}
  )

  const {
    data: yesterdayKpis = [],
    isLoading: isYesterdayKpisLoading,
    isError: isYesterdayKpisError,
    isRefetching: isYesterdayKpisRefetching,
    refetch: refetchYesterdayKpis
  } = useGetKpisByTypes({
    plantId,
    kpiVisibilityFlag: kpiVisibilityFlag,
    kpiTypes: kpiTypes,
    timeFrame: {
      startDate: startOfYesterday.toISOString(),
      endDate: endOfYesterday.toISOString(),
      calculationTimeFrameType: CalculationTimeFrameType.Yesterday
    }
  })

  const kpisMonthlyTrendsItems = useMemo(
    () =>
      kpisMonthlyTrends?.map((kpiTrend) => ({
        key: getKpiTypeKey(kpiTrend.kpiType, kpiTrend.kpiType, kpiTrend.equipmentName),
        label: formatKpiLabel({label: kpiTrend.kpiType, equipmentName: kpiTrend.equipmentName}, t),
        monthlyTrendDataUnit: kpiTrend.unit,
        monthlyTrendData: mapToKpiTrendChartData(kpiTrend)
      })) ?? [],
    [kpisMonthlyTrends, t]
  )

  return (
    <CardBox
      sx={{
        ...cardBoxSizes['M'],
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        px: 0,
        pl: cardBoxPx
      }}
      data-test-id="plant-status-kpis-card"
    >
      <Box
        sx={{
          display: 'flex',
          pr: cardBoxPx
        }}
      >
        <Box flex={1}>
          <Typography variant="h3" mb={2}>
            {t('plantStatus.cardTitle')}
          </Typography>
        </Box>
        <CardChipsToggle
          options={cardVariants}
          value={cardVariant}
          onChange={handleCardVariantChange}
        />
      </Box>
      {cardVariant === PlantStatusVariant.YESTERDAY && (
        <DataContentWrapper
          showLoader={isYesterdayKpisLoading}
          isError={isYesterdayKpisError}
          isRefetching={isYesterdayKpisRefetching}
          data={yesterdayKpis}
          retryFunction={() => void refetchYesterdayKpis()}
          progressSx={{color: 'primary.main'}}
        >
          <Box sx={{flex: 1, overflowY: 'auto', pr: cardBoxPx}}>
            <PlantStatusDailyKpis
              kpiItems={yesterdayKpis}
              showKpiValueLoader={isYesterdayKpisLoading}
              gridColumns={10}
              gridItemXs={2}
            />
          </Box>
        </DataContentWrapper>
      )}
      {cardVariant === PlantStatusVariant.TRENDS && (
        <DataContentWrapper
          showLoader={areKpisTrendsLoading}
          isError={isKpisTrendsError}
          isRefetching={areKpisTrendsRefetching}
          data={kpisMonthlyTrends}
          retryFunction={() => void refetchKpisTrends()}
          progressSx={{color: 'primary.main'}}
        >
          <Box sx={{flex: 1, overflowY: 'auto', pr: cardBoxPx}}>
            <PlantStatusKpisTrends
              kpiItems={kpisMonthlyTrendsItems}
              areMonthlyKpiTrendsLoading={areKpisTrendsLoading}
              gridColumns={10}
              gridItemXs={2}
            />
          </Box>
        </DataContentWrapper>
      )}
    </CardBox>
  )
}
